import React, { useState, useEffect } from 'react';
import { Menu, Calendar, CloudSun, Clock } from 'lucide-react';
import AdBanner from '../common/AdBanner';
import { adApi } from '../../utils/api';
import { useSiteSettings } from '../../hooks/useSiteSettings';

const NP_DIGITS = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
const npDigits = (s) => String(s).replace(/\d/g, (d) => NP_DIGITS[+d]);
const MONTHS_NP = ['जनवरी', 'फेब्रुअरी', 'मार्च', 'अप्रिल', 'मे', 'जुन', 'जुलाई', 'अगस्ट', 'सेप्टेम्बर', 'अक्टोबर', 'नोभेम्बर', 'डिसेम्बर'];
const DAYS_NP = ['आइतबार', 'सोमबार', 'मङ्गलबार', 'बुधबार', 'बिहीबार', 'शुक्रबार', 'शनिबार'];

// WMO weather codes -> Nepali labels
const weatherLabel = (code) => {
  if (code === 0) return 'सफा आकाश';
  if (code <= 3) return 'आंशिक बदली';
  if (code <= 48) return 'कुहिरो';
  if (code <= 57) return 'सिमसिम पानी';
  if (code <= 67) return 'वर्षा';
  if (code <= 77) return 'हिमपात';
  if (code <= 82) return 'वर्षा झरी';
  if (code >= 95) return 'चट्याङसहित वर्षा';
  return '';
};

// Weather API URL .env bata aauncha (REACT_APP_WEATHER_API_URL)
const WEATHER_URL =
  process.env.REACT_APP_WEATHER_API_URL ||
  'https://api.open-meteo.com/v1/forecast?latitude=27.7172&longitude=85.324&current=temperature_2m,weather_code';

function Header({ scrolled, hidden, onMenuOpen, onGoHome }) {
  const settings = useSiteSettings();
  const [now, setNow] = useState(new Date());
  const [weather, setWeather] = useState(null);
  const [hasTopAd, setHasTopAd] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Header ad chha ki chhaina — chhaina bhane logo + date/time/weather center ma
  useEffect(() => {
    let mounted = true;
    adApi
      .getByPosition('topright')
      .then((res) => {
        if (mounted) setHasTopAd(!!(res.data && res.data.length > 0));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  // Weather API (URL .env: REACT_APP_WEATHER_API_URL) — Kathmandu
  useEffect(() => {
    let mounted = true;
    fetch(WEATHER_URL)
      .then((r) => r.json())
      .then((d) => {
        if (mounted && d?.current) {
          setWeather({
            temp: Math.round(d.current.temperature_2m),
            label: weatherLabel(d.current.weather_code),
          });
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const dateStr = `${DAYS_NP[now.getDay()]}, ${npDigits(now.getDate())} ${MONTHS_NP[now.getMonth()]} ${npDigits(now.getFullYear())}`;
  const h24 = now.getHours();
  const partOfDay = h24 < 12 ? 'बिहान' : h24 < 16 ? 'दिउँसो' : h24 < 20 ? 'साँझ' : 'राति';
  const timeStr = `${npDigits(String(h24 % 12 || 12).padStart(2, '0'))}:${npDigits(String(now.getMinutes()).padStart(2, '0'))} ${partOfDay}`;

  return (
    <header
      className={`sticky top-0 z-40 w-full bg-white/95 backdrop-blur-[10px] border-b border-line transition-all duration-300 ${
        hidden ? 'lg:-translate-y-full' : 'lg:translate-y-0'
      } ${scrolled ? 'py-2 shadow-sm' : 'py-3'}`}
    >
      {/* Header background photo (admin bata) — readable overlay sahit */}
      {settings.header_bg_photo && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <img
            src={settings.header_bg_photo}
            alt=""
            className={`w-full object-cover object-center ${scrolled ? 'opacity-0 transition-opacity duration-300' : 'opacity-100'}`}
            style={{ height: '100%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/65 to-white/30" />
        </div>
      )}
      <div className={`relative max-w-[1240px] mx-auto px-5 flex items-center gap-5 sm:gap-6 xl:gap-9 transition-all duration-200 ${
          scrolled ? 'py-2' : 'py-3'
        } ${hasTopAd ? '' : 'justify-center'}`}
      >
        {/* Logo */}
        <div className="flex items-center min-w-0">
          <a
            href="#"
            className="flex items-center flex-shrink-0"
            onClick={(e) => {
              e.preventDefault();
              onGoHome();
            }}
            aria-label="Kalo Jhola गृहपृष्ठ"
          >
            {settings.header_logo ? (
              <img src={settings.header_logo} alt="Kalo Jhola" className={`w-auto object-contain ${scrolled ? 'h-16' : 'h-24'}`} />
            ) : (
              <span className={`font-display font-bold tracking-tight text-ink whitespace-nowrap transition-all ${scrolled ? 'text-[24px]' : 'text-[34px]'}`}>
                KALO<em className="not-italic text-crimson">JHOLA</em>
              </span>
            )}
          </a>
        </div>

        {/* Desktop: date · time · weather — logo sathi nai thulo size ma */}
        <div className="hidden xl:flex flex-col items-start gap-0.5 leading-snug text-[14.5px] text-ink-soft whitespace-nowrap min-w-0">
          <span className="flex items-center gap-2 font-semibold">
            <Calendar size={17} /> {dateStr}
          </span>
          <span className="flex items-center gap-2">
            <Clock size={17} /> {timeStr}
          </span>
          <span className="flex items-center gap-2 text-gold font-semibold">
            <CloudSun size={18} />{' '}
            {weather ? `${npDigits(weather.temp)}°C${weather.label ? `, ${weather.label}` : ''}, काठमाडौं` : 'मौसम लोड हुँदै..., काठमाडौं'}
          </span>
        </div>

        {/* Header ad — दायाँ छेउमा ठूलो; ad chhaina bhane dekhaindaina ra baki center hunchha */}
        {hasTopAd && (
          <div className="hidden lg:flex items-center ml-auto min-w-0 max-w-full">
            <AdBanner position="topright" noFallback />
          </div>
        )}

        {/* Mobile/Tablet: mini date · time · weather — header ko bheetra center ma */}
        <div className="lg:hidden flex-1 flex flex-col items-center justify-center text-center leading-tight min-w-0 px-1">
          <span className="text-[9.5px] font-semibold text-ink-soft whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
            {dateStr} • {timeStr}
          </span>
          <span className="mt-0.5 inline-flex items-center gap-1 text-[9.5px] font-semibold text-gold whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
            <CloudSun size={11} className="flex-shrink-0" />
            {weather ? `${npDigits(weather.temp)}°C${weather.label ? `, ${weather.label}` : ''}, काठमाडौं` : 'मौसम लोड हुँदै..., काठमाडौं'}
          </span>
        </div>

        {/* Menu button — mobile ma right pin */}
        <button
          className="ml-auto inline-flex items-center justify-center w-10 h-10 rounded-[10px] text-ink hover:bg-paper-soft lg:hidden flex-shrink-0"
          onClick={onMenuOpen}
          aria-label="Open menu"
        >
          <Menu size={22} />
        </button>
      </div>
    </header>
  );
}

export default Header;
